import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AppBreadcrumb, AppButton, AppCard, AppContainer } from '@/components/atoms';
import { EditFill } from '@/components/atoms/AppIcon';
import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';
import { getLatlonCache } from '@/services/admin/latlon-caches';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import { AuditInfo } from '@/components/molecules';

const SiteAdminLatlonCachesDetailPage: React.FC = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const currentSite = site ?? 'super-admin';
  const recordId = Number(id) || 0;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const listSearch = (location.state as { listSearch?: string })?.listSearch ?? '';
  const listPath = `/${currentSite}/admin/latlon_caches${listSearch}`;

  const { data, status, isFetching, isError, error } = useQuery({
    queryKey: ['admin', 'latlon-cache-detail', currentSite, recordId],
    queryFn: () => getLatlonCache(currentSite, recordId),
    enabled: !!recordId,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, 'データの取得に失敗しました。'));
    }
  }, [isError, error, toast]);

  const record = data?.data;
  const isInitialLoading = status === 'pending' || (isFetching && !record);

  if (isInitialLoading) return <LoadingTemplate />;
  if (status === 'error' || (!isFetching && !record)) return <SiteAdminNotFoundTemplate />;

  const breadcrumbItems = [
    { title: '緯度経度キャッシュ', href: listPath },
    { title: `詳細:${recordId}` },
  ];

  const fields = [
    { label: 'id', value: record?.id },
    { label: '治療院ID', value: record?.clinic_number },
    { label: '住所', value: record?.address },
    { label: 'GeOAP住所', value: record?.address_geop },
    { label: '緯度', value: record?.latitude },
    { label: '経度', value: record?.longitude },
    { label: '作成日', value: record?.created_at },
    { label: '更新日', value: record?.updated_at },
  ];

  const handleEdit = () => {
    navigate(`/${currentSite}/admin/latlon_caches/${recordId}/edit`, {
      state: { listSearch, fromDetail: true },
    });
  };

  const handleBack = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'latlon-caches'] });
    navigate(listPath);
  };

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} />

      <AppCard title={`緯度経度キャッシュ詳細:${recordId}`}>
        <dl className="grid grid-cols-12 gap-y-2 text-sm">
          {fields.map((item, index) => (
            <div key={index} className="contents">
              <dt className="col-span-2 font-semibold">{item.label}</dt>
              <dd className="col-span-10">{item.value ?? ''}</dd>
            </div>
          ))}
        </dl>

        <AuditInfo
          createdBy={record?.created_by}
          updatedBy={record?.updated_by}
          updatedAt={record?.updated_at}
        />

        <div className="mt-5 flex gap-3">
          <AppButton type="primary" icon={<EditFill />} onClick={handleEdit}>
            編集
          </AppButton>
          <AppButton onClick={handleBack}>
            前画面に戻る
          </AppButton>
        </div>
      </AppCard>
    </AppContainer>
  );
};

export default SiteAdminLatlonCachesDetailPage;
